import { useQuery } from "@apollo/client";
import useAppChannel from "@dashboard/components/AppLayout/AppChannelContext";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { analyticsDashboardQuery } from "../../analyticsQueries";
import { AnalyticsFilterBar } from "../../components/AnalyticsFilterBar/AnalyticsFilterBar";
import { CustomerAnalytics } from "../../components/CustomerAnalytics/CustomerAnalytics";
import { FinanceDashboard } from "../../components/FinanceDashboard/FinanceDashboard";
import { InventoryInsights } from "../../components/InventoryInsights/InventoryInsights";
import { LedgerReportSummary } from "../../components/LedgerReportSummary/LedgerReportSummary";
import { ProductPerformance } from "../../components/ProductPerformance/ProductPerformance";
import { SalesDashboard } from "../../components/SalesDashboard/SalesDashboard";
import { AnalyticsQueryResult } from "../../types";
import { AnalyticsPeriod, AnalyticsTab, analyticsUrl } from "../../urls";
import { downloadLedgerReport } from "../../utils/downloadReport";

export const AnalyticsPage: React.FC = () => {
  const navigate = useNavigator();
  const { channel } = useAppChannel();

  // Read initial params from URL search
  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = (searchParams.get("tab") as AnalyticsTab) || "sales";
  const initialPeriod = (searchParams.get("period") as AnalyticsPeriod) || "month";

  const today = new Date().toISOString().split("T")[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];

  const [activeTab, setActiveTab] = useState<AnalyticsTab>(initialTab);
  const [period, setPeriod] = useState<AnalyticsPeriod>(initialPeriod);
  const [startDate, setStartDate] = useState<string>(
    searchParams.get("startDate") || thirtyDaysAgo,
  );
  const [endDate, setEndDate] = useState<string>(searchParams.get("endDate") || today);
  const [slot, setSlot] = useState<string>(searchParams.get("slot") || "ALL");
  const [downloadingFormat, setDownloadingFormat] = useState<"pdf" | "csv" | null>(null);

  const notify = useNotifier();
  const channelSlug = channel?.slug || "pune";

  // Map period string to uppercase enum value for GraphQL
  const periodEnumMap: Record<AnalyticsPeriod, string> = {
    day: "DAY",
    month: "MONTH",
    year: "YEAR",
    custom: "CUSTOM",
    ledger: "LEDGER",
  };

  const { data, loading, error, refetch } = useQuery<AnalyticsQueryResult>(
    analyticsDashboardQuery,
    {
      variables: {
        period: periodEnumMap[period] || "MONTH",
        startDate:
          period === "custom" || period === "ledger" || period === "day" ? startDate : undefined,
        endDate: period === "custom" || period === "ledger" ? endDate : undefined,
        channel: channelSlug,
        slot: slot !== "ALL" ? slot : undefined,
      },
      fetchPolicy: "network-only",
    },
  );

  const updateUrl = (
    newTab: AnalyticsTab,
    newPeriod: AnalyticsPeriod,
    newStart: string,
    newEnd: string,
    newSlot: string,
  ) => {
    navigate(
      analyticsUrl({
        tab: newTab,
        period: newPeriod,
        startDate: newStart,
        endDate: newEnd,
        slot: newSlot,
        channel: channelSlug,
      }),
      { replace: true },
    );
  };

  const handleTabChange = (tab: AnalyticsTab) => {
    setActiveTab(tab);
    updateUrl(tab, period, startDate, endDate, slot);
  };

  const handlePeriodChange = (newPeriod: AnalyticsPeriod) => {
    setPeriod(newPeriod);
    updateUrl(activeTab, newPeriod, startDate, endDate, slot);
  };

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    updateUrl(activeTab, period, val, endDate, slot);
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    updateUrl(activeTab, period, startDate, val, slot);
  };

  const handleSlotChange = (newSlot: string) => {
    setSlot(newSlot);
    updateUrl(activeTab, period, startDate, endDate, newSlot);
  };

  const handleDownloadReport = async (format: "pdf" | "csv") => {
    try {
      setDownloadingFormat(format);

      const resolvedStart = analytics?.startDate || startDate;
      const resolvedEnd = analytics?.endDate || endDate;

      await downloadLedgerReport({
        format,
        startDate: resolvedStart,
        endDate: resolvedEnd,
        period,
      });

      notify({
        status: "success",
        text: `Ledger ${format.toUpperCase()} report downloaded successfully.`,
      });
    } catch (err: any) {
      notify({
        status: "error",
        text: err?.message || `Failed to download ${format.toUpperCase()} report.`,
      });
    } finally {
      setDownloadingFormat(null);
    }
  };

  const analytics = data?.analytics;
  const currency = analytics?.currency || "INR";

  return (
    <Box padding={6} display="flex" flexDirection="column" gap={4}>
      {/* Page Title & Status */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" flexDirection="column" gap={1}>
          <Text as="h1" size={8} fontWeight="bold">
            Store Analytics & Business Intelligence
          </Text>
          <Text size={3} color="default1">
            Omnichannel Sales, Products, Customers, Inventory & Ledger Reconciliation
          </Text>
        </Box>
        {analytics && (
          <Box display="flex" alignItems="center" gap={2}>
            <Text size={2} color="default1">
              Active Range:
            </Text>
            <Text size={2} fontWeight="bold">
              {analytics.startDate} to {analytics.endDate}
            </Text>
          </Box>
        )}
      </Box>

      {/* Filter Toolbar */}
      <AnalyticsFilterBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        period={period}
        onPeriodChange={handlePeriodChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        slot={slot}
        onSlotChange={handleSlotChange}
        onRefresh={() => refetch()}
        loading={loading}
        onDownloadReport={handleDownloadReport}
        downloadingFormat={downloadingFormat}
      />

      {/* Loading State */}
      {loading && !analytics && (
        <Box
          padding={10}
          display="flex"
          justifyContent="center"
          alignItems="center"
          backgroundColor="default1"
          borderRadius={3}
        >
          <Text size={4} color="default1">
            Loading real-time analytics data...
          </Text>
        </Box>
      )}

      {/* Error State */}
      {error && !analytics && (
        <Box
          padding={6}
          backgroundColor="default2"
          borderRadius={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold" color="critical1">
            Unable to load analytics
          </Text>
          <Text size={2} color="default1">
            {error.message}
          </Text>
          <Button variant="primary" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        </Box>
      )}

      {/* Main Tab Views */}
      {analytics && (
        <>
          {activeTab === "sales" && <SalesDashboard data={analytics.sales} currency={currency} />}
          {activeTab === "products" && (
            <ProductPerformance data={analytics.products} currency={currency} />
          )}
          {activeTab === "customers" && (
            <CustomerAnalytics data={analytics.customers} currency={currency} />
          )}
          {activeTab === "inventory" && (
            <InventoryInsights data={analytics.inventory} currency={currency} />
          )}
          {activeTab === "finance" && (
            <FinanceDashboard
              data={analytics.finance}
              currency={currency}
              onDownloadReport={handleDownloadReport}
              downloadingFormat={downloadingFormat}
            />
          )}
          {activeTab === "ledger" && (
            <LedgerReportSummary
              startDate={analytics.startDate || startDate}
              endDate={analytics.endDate || endDate}
              period={period}
              currency={currency}
              onDownloadReport={handleDownloadReport}
              downloadingFormat={downloadingFormat}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default AnalyticsPage;
